import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DollarSign } from 'lucide-react';
import { ProfileLink } from '@/components/ProfileLink';

interface FeedEntry {
    id: number;
    type: string;
    description: string;
    amount: string;
    effective_at: string;
    contract_title: string | null;
    sub: { id: number; display_name: string; avatar_path?: string } | null;
    dom: { id: number; display_name: string; avatar_path?: string } | null;
}

interface FeedResponse {
    data: FeedEntry[];
    meta: { current_page: number; last_page: number };
}

function timeAgo(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
}

export default function FeeTaxFeedPage() {
    const { data, isLoading } = useQuery({
        queryKey: ['fee-tax-feed'],
        queryFn: () => api.get<FeedResponse>('/feed'),
    });

    const entries = data?.data ?? [];

    return (
        <div className="space-y-6">
            <div className="text-center">
                <h1 className="flex items-center justify-center gap-2 text-2xl font-bold text-amber-400">
                    <DollarSign className="h-6 w-6" />
                    Fee & Tax Feed
                    <DollarSign className="h-6 w-6" />
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                    Live feed of fees and taxes assessed across the platform
                </p>
            </div>

            {isLoading && (
                <p className="text-center text-sm text-slate-500">Loading...</p>
            )}

            {!isLoading && entries.length === 0 && (
                <Card>
                    <CardContent className="py-12 text-center text-sm text-slate-500">
                        No fees or taxes posted to the feed yet.
                    </CardContent>
                </Card>
            )}

            <div className="space-y-2">
                {entries.map((entry) => (
                    <Card key={entry.id}>
                        <CardContent className="py-3">
                            <div className="flex items-center gap-3">
                                {/* Avatar */}
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-900/30 text-amber-400">
                                    {entry.sub?.avatar_path ? (
                                        <img
                                            src={entry.sub.avatar_path}
                                            alt=""
                                            className="h-10 w-10 rounded-full object-cover"
                                        />
                                    ) : (
                                        <DollarSign className="h-5 w-5" />
                                    )}
                                </div>

                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 text-sm">
                                        {entry.sub ? (
                                            <ProfileLink userId={entry.sub.id} displayName={entry.sub.display_name} className="font-medium text-slate-200 hover:underline" />
                                        ) : (
                                            <span className="font-medium text-slate-200">Unknown</span>
                                        )}
                                        <span className="text-slate-500">was hit with</span>
                                        <Badge
                                            variant="outline"
                                            className={entry.type === 'tax'
                                                ? 'border-purple-700 text-purple-400'
                                                : 'border-amber-700 text-amber-400'
                                            }
                                        >
                                            {entry.type}
                                        </Badge>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="font-semibold text-amber-400">
                                            ${parseFloat(entry.amount).toFixed(2)}
                                        </span>
                                        <span className="text-slate-400">{entry.description}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-slate-500">
                                        {entry.dom && (
                                            <span>by <ProfileLink userId={entry.dom.id} displayName={entry.dom.display_name} className="text-violet-400 hover:underline text-xs" /></span>
                                        )}
                                        <span>{timeAgo(entry.effective_at)}</span>
                                    </div>
                                </div>

                                <div className="shrink-0 text-right">
                                    <span className="text-lg font-bold text-amber-400">
                                        +${parseFloat(entry.amount).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}
